import { Label, OrderContainer, OrderItem, OrderItemCell, StyledLink } from './styledComponents';

import { Pagination } from 'antd';
import messages from 'containers/OrdersPage/messages';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Order, OrderStatus } from 'types/schema';
import calculateVAT from 'utils/calculateVAT';
import generateProductSlug from 'utils/generateProductSlug';
import { getUomNameLocal } from 'utils/getUomNameLocal';
import utilsMessages from 'utils/messages';
import { removeParentheses } from 'utils/strings';
import Money from '../Money/Money';
import ItemQuantity from './ItemQuantity';
import PriceByStatus from './PriceByStatus';
import noImage from './noIMG.svg';

interface DesktopTableProps {
  order: Order;
}

const Note = styled.p`
  color: #8c8c8c;
`;

const ProductImage = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 4px;
`;

const ProductLink = styled(StyledLink)`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 16px 0;
`;

const DesktopTable: FC<DesktopTableProps> = (props) => {
  const { order } = props;
  const lang = localStorage.getItem('lang') || 'vi';
  const isShowItemDiscountValue =
    !!order.discount && [OrderStatus.Delivering, OrderStatus.Delivered].includes(order.status);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const totalItems = order.items.length;

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const currentItems = order.items.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <OrderContainer>
      <OrderItem className="header">
        <OrderItemCell key="Product" fbasis={isShowItemDiscountValue ? '30%' : '35%'}>
          <FormattedMessage {...messages.product} /> ({order.items.length})
        </OrderItemCell>
        <OrderItemCell key="Price" fbasis="15%" textAlign="right">
          <FormattedMessage {...utilsMessages.priceExcludedVat} values={{ break: <br /> }} />
        </OrderItemCell>
        {isShowItemDiscountValue && (
          <OrderItemCell key="PriceAfterDiscount" fbasis="10%">
            <FormattedMessage {...utilsMessages.priceAfterDiscount} />
          </OrderItemCell>
        )}
        <OrderItemCell key="Quantity" fbasis="10%" textAlign="right">
          <FormattedMessage {...utilsMessages.quantity} />
        </OrderItemCell>
        <OrderItemCell key="TotalBeforeVat" fbasis="15%" textAlign="right">
          <FormattedMessage {...utilsMessages.totalExclVat} values={{ break: <br /> }} />
        </OrderItemCell>
        <OrderItemCell key="Vat" fbasis="5%">
          VAT
        </OrderItemCell>
        <OrderItemCell key="TotalAfterVat" fbasis="15%" textAlign="right">
          <FormattedMessage {...utilsMessages.totalIncludedVat} values={{ break: <br /> }} />
        </OrderItemCell>
        {isShowItemDiscountValue && (
          <OrderItemCell key="TotalAfterDiscount" fbasis="15%">
            <FormattedMessage {...utilsMessages.totalAfterDiscountInclVAT} />
          </OrderItemCell>
        )}
        {/* <OrderItemCell key="Notes" fbasis="5%" /> */}
      </OrderItem>
      {currentItems.map((item: Order, _index: number) => {
        const {
          product: { vat, uomLocal, name, supplierInternalCode },
          buyerNote,
          deliveryQuantity,
          total,
          // deliveredNote,
          priceV2,
          orderedPriceV2,
          orderedQuantity,
          totalBeforeVat,
          unitPriceAfterDiscount,
          amountAfterDiscount,
        } = item;

        return (
          <OrderItem key={`${startIndex}-${_index}`}>
            <OrderItemCell
              key="Product"
              fbasis={isShowItemDiscountValue ? '30%' : '35%'}
              flexDirection={buyerNote ? 'column' : 'row'}
            >
              <ProductLink title={name} to={`/products/${generateProductSlug(name, supplierInternalCode, lang)}`}>
                <ProductImage src={item.product.imageUrl || noImage} alt={item.product.name} />
                {name}
              </ProductLink>
              {buyerNote && <Note>{buyerNote}</Note>}
            </OrderItemCell>
            <OrderItemCell key="Price" fbasis="15%" textAlign="right">
              <PriceByStatus
                orderedPrice={
                  calculateVAT(
                    orderedPriceV2?.price || 0,
                    orderedPriceV2?.vat || 0,
                    !!orderedPriceV2?.vatIncluded,
                  ).beforeVat.toString() || ''
                }
                deliveredPrice={
                  calculateVAT(priceV2?.price || 0, priceV2?.vat || 0, !!priceV2?.vatIncluded).beforeVat.toString() ||
                  ''
                }
                status={order.status}
              />
            </OrderItemCell>
            {isShowItemDiscountValue && (
              <OrderItemCell key="PriceAfterDiscount" fbasis="10%">
                <Money>{Number(unitPriceAfterDiscount)}</Money>
              </OrderItemCell>
            )}
            <OrderItemCell key="Quantity" fbasis="10%">
              <ItemQuantity
                orderedQuantity={Number(orderedQuantity)}
                deliveryQuantity={Number(deliveryQuantity)}
                uom={removeParentheses(getUomNameLocal(uomLocal || ''))}
              />
            </OrderItemCell>
            <OrderItemCell key="TotalBeforeVat" fbasis="15%" textAlign="right">
              <Money>{totalBeforeVat || 0}</Money>
            </OrderItemCell>
            <OrderItemCell key="Vat" fbasis="5%">
              {vat}%
            </OrderItemCell>
            <OrderItemCell key="TotalAfterVat" fbasis="15%" textAlign="right">
              <span>
                <Money>{total || 0}</Money>
              </span>
            </OrderItemCell>
            {isShowItemDiscountValue && (
              <OrderItemCell key="TotalAfterDiscount" fbasis="15%">
                <span>
                  <Money>{Number(amountAfterDiscount) || 0}</Money>
                  <br />
                  <Label>( {vat}% VAT )</Label>
                </span>
              </OrderItemCell>
            )}
            {/* <OrderItemCell key="Notes" fbasis="5%">
              {deliveredNote && (
                <Popover content={<pre>{deliveredNote}</pre>}>
                  <NoteIcon src={Notes} note="true" />
                </Popover>
              )}
            </OrderItemCell> */}
          </OrderItem>
        );
      })}

      {totalItems > pageSize && (
        <PaginationContainer>
          <Pagination
            current={currentPage}
            total={totalItems}
            pageSize={pageSize}
            onChange={handlePageChange}
            showSizeChanger={false}
          />
        </PaginationContainer>
      )}
    </OrderContainer>
  );
};

export default DesktopTable;
